import type {
  FiscalBenefitRunSummary,
  Form2181Export,
} from "@fiscalo/shared-types";

import { apiClient } from "@/services/apiClient";

export type {
  FiscalBenefitRunSummary,
  Form2181Export,
  Form2181ExportRow,
} from "@fiscalo/shared-types";

export async function getForm2181Export(
  period: string,
): Promise<Form2181Export> {
  const { data } = await apiClient.get<Form2181Export>(
    "/platform/fiscal-benefit/2181-export",
    { params: { period } },
  );
  return data;
}

/**
 * Which credits the PADI file describes.
 *
 * `auto` — what the page uses — means "this period's file as it stands": the
 * ledger once anything has been granted, otherwise the projection, so the file
 * can be validated in PADI before Otorgar emits anything irreversible. The
 * caller does not choose; the backend reports which it returned.
 *
 * `ledger` and `simulation` force one side, for scripts and for debugging a
 * disagreement between the two.
 */
export type Form2181PadiSource = "auto" | "ledger" | "simulation";

/**
 * The PADI import file plus the two reconciliation figures the backend reports
 * as headers.
 *
 * `centavosDropped` should always be 0 — credits are rounded to whole pesos when
 * granted. A non-zero value means a credit carries centésimos, so the ledger and
 * the client's CFE/e-Resguardo already disagree with what this file would
 * declare; surfaced so an operator sees that before importing, not after.
 */
export async function getForm2181PadiTxt(
  period: string,
  source: Form2181PadiSource = "auto",
): Promise<{
  txt: string;
  rows: number;
  centavosDropped: number;
  /** Which set the backend actually returned. */
  returnedSource: "ledger" | "simulation";
}> {
  const res = await apiClient.get<string>(
    "/platform/fiscal-benefit/2181-export.txt",
    { params: { period, source }, responseType: "text" },
  );
  const header = (name: string): number =>
    Number(res.headers?.[name] ?? 0) || 0;
  return {
    txt: typeof res.data === "string" ? res.data : String(res.data ?? ""),
    rows: header("x-fiscalo-2181-rows"),
    centavosDropped: header("x-fiscalo-2181-centavos-dropped"),
    returnedSource:
      res.headers?.["x-fiscalo-2181-source"] === "simulation"
        ? "simulation"
        : "ledger",
  };
}

/**
 * The declaration as the PRESENTATION file — the format PADI emits for upload
 * to Servicios en linea, so the desktop round-trip can be skipped. The import
 * .txt above stays useful for validating a period IN PADI before filing.
 *
 * The filename matters: DGI's is F<formulario>_<rut>_<periodo>_<crc>, and the
 * CRC in it must match the one inside the file, so it comes from the server
 * rather than being rebuilt here.
 */
export async function getForm2181Presentacion(
  period: string,
): Promise<{ txt: string; filename: string; crc: string; rows: number }> {
  const res = await apiClient.get<string>(
    "/platform/fiscal-benefit/2181-presentacion.txt",
    { params: { period }, responseType: "text" },
  );
  const disposition = String(res.headers?.["content-disposition"] ?? "");
  const match = /filename="([^"]+)"/.exec(disposition);
  return {
    txt: typeof res.data === "string" ? res.data : String(res.data ?? ""),
    filename: match?.[1] ?? `F2181_${period.replace("-", "")}.txt`,
    crc: String(res.headers?.["x-fiscalo-2181-crc"] ?? ""),
    rows: Number(res.headers?.["x-fiscalo-2181-rows"] ?? 0) || 0,
  };
}

/**
 * Grant the period's credits. Defaults to a dry run: the backend computes
 * eligibility and amounts without issuing CFEs/resguardos or writing the
 * ledger, which is the only safe way to inspect a period before it is filed.
 */
export async function runFiscalBenefit(
  period: string,
  dryRun: boolean,
): Promise<FiscalBenefitRunSummary> {
  const { data } = await apiClient.post<FiscalBenefitRunSummary>(
    "/platform/fiscal-benefit/run",
    { period, dryRun },
  );
  return data;
}

/**
 * The 80 UI benefit's window end, decreto and per-year credit table — the
 * three facts root can change without a deploy. `firstPeriod` is fixed
 * history and not editable; only `lastPeriod` moves when the decreto is
 * extended.
 */
export interface FiscalBenefitConfig {
  window: { firstPeriod: string; lastPeriod: string };
  decreto: string;
  /** UYU minor units, keyed by calendar year. */
  creditByYear: Record<number, number>;
}

export async function getFiscalBenefitConfig(): Promise<FiscalBenefitConfig> {
  const { data } = await apiClient.get<FiscalBenefitConfig>(
    "/platform/fiscal-benefit/config",
  );
  return data;
}

/**
 * How many tenants a change to a year's credit would touch. Call this BEFORE
 * setCredit, so root sees the blast radius rather than discovering it in a
 * billing surprise: saving repricess real dLocal subscriptions.
 */
export interface FiscalBenefitCreditImpact {
  year: number;
  literalETenantCount: number;
  distinctPlanCount: number;
  /**
   * Whether `year` is the one actually used to price live subscriptions right
   * now. No company row is tagged with a "pricing year" — every live charge
   * resolves the CURRENT calendar year's credit at request time — so editing
   * any OTHER year (already-filed history, or a future year with no live
   * charge yet) changes nothing for anyone today. When false, the counts
   * above are both 0, not because nobody is affected, but because that year
   * isn't the one in effect.
   */
  appliesToCurrentPricing: boolean;
}

export async function getFiscalBenefitCreditImpact(
  year: number,
): Promise<FiscalBenefitCreditImpact> {
  const { data } = await apiClient.get<FiscalBenefitCreditImpact>(
    "/platform/fiscal-benefit/config/credit-impact",
    { params: { year } },
  );
  return data;
}

/**
 * Move the window's end. The backend refuses a year with no credit value
 * loaded, and refuses shortening below a period that already has granted
 * credits — errors surface via the axios rejection's response body.
 */
export async function setFiscalBenefitWindowEnd(
  lastPeriod: string,
): Promise<FiscalBenefitConfig> {
  const { data } = await apiClient.post<FiscalBenefitConfig>(
    "/platform/fiscal-benefit/config/window-end",
    { lastPeriod },
  );
  return data;
}

/** Set (or overwrite) a year's 80 UI credit, in whole UYU (not minor units). */
export async function setFiscalBenefitCredit(
  year: number,
  amountMajor: number,
): Promise<FiscalBenefitConfig> {
  const { data } = await apiClient.post<FiscalBenefitConfig>(
    "/platform/fiscal-benefit/config/credit",
    { year, amountMajor },
  );
  return data;
}

/** Set the decreto new declarations are stamped with. */
export async function setFiscalBenefitDecreto(
  decreto: string,
): Promise<FiscalBenefitConfig> {
  const { data } = await apiClient.post<FiscalBenefitConfig>(
    "/platform/fiscal-benefit/config/decreto",
    { decreto },
  );
  return data;
}

// ---------------------------------------------------------------------------
// Form 2181 envío to Servicios en línea (spec 858fc66b)
//
// These deliberately do NOT reuse the /fiscal/dgi-consulta session endpoints:
// those resolve the caller's ACTIVE TENANT and require an Ultra plan, so a
// platform admin driving a Fiscalo-owned session gets a 404 from them.
// ---------------------------------------------------------------------------

export interface StartForm2181EnvioResult {
  sessionId: string;
  filename: string;
  crc: string;
  rows: number;
  /**
   * The file was built from a SIMULATION because the period is not otorgado
   * yet, so the comprobantes still have to be emitted once DGI takes it.
   */
  fromProjection: boolean;
}

/** Start the interactive upload of a period's presentation file. */
export async function startForm2181Envio(
  period: string,
): Promise<StartForm2181EnvioResult> {
  const { data } = await apiClient.post<StartForm2181EnvioResult>(
    "/platform/fiscal-benefit/2181-envio",
    {},
    { params: { period } },
  );
  return data;
}

export interface Form2181EnvioSession {
  status?: string;
  currentStep?: string;
  lastError?: string | null;
  /** Verbatim text DGI's page rendered after Enviar. */
  rawMessage?: string;
  /**
   * Whether the flow RECOGNISED the page's success message — NOT an
   * accept/reject verdict, which DGI sends by email. Never render as "aceptado".
   */
  outcomeConfirmed?: boolean;
  /** DGI's receipt for the presentation ("NÚMERO" on the result page). */
  dgiNumero?: string;
  /** The CRC DGI echoed back; must equal the one in our filename. */
  dgiCrc?: string;
}

export async function getForm2181EnvioSession(
  sessionId: string,
): Promise<Form2181EnvioSession> {
  const { data } = await apiClient.get<Form2181EnvioSession>(
    `/platform/fiscal-benefit/2181-envio/session/${sessionId}`,
  );
  return data ?? {};
}

/** Feed the interactive SSO the operator's IDP choice / document number. */
export async function submitForm2181EnvioStep(payload: {
  sessionId: string;
  idpChoice?: "abitab" | "tuid";
  abitabDocumentType?: "cedula" | "pasaporte";
  abitabDocumentNumber?: string;
  tuidAppMobileConfirmed?: boolean;
  tuidDocument?: string;
}): Promise<void> {
  await apiClient.post("/platform/fiscal-benefit/2181-envio/step", payload);
}

export async function cancelForm2181Envio(sessionId: string): Promise<void> {
  await apiClient.post("/platform/fiscal-benefit/2181-envio/cancel", {
    sessionId,
  });
}
